import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Testimonial } from '../types';
import { getRandomTestimonials, submitContactEmail } from '../services/api';
import { trackEvent } from '../analytics';

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(to bottom, #ffffff 0%, #f9f5f0 100%);
  position: relative;
  color: #111827;
`;

const HeroWrapper = styled.div`
  position: relative;
  width: 100%;
  background-image: url('/landing_page_packages.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  flex-direction: column;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0.8) 0%,
    rgba(255, 255, 255, 0.7) 38%,
    rgba(255, 255, 255, 0.6) 60%,
    rgba(255, 255, 255, 0.2) 100%
  );
  pointer-events: none;
`;

const Content = styled.main`
  position: relative;
  z-index: 1;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  width: 100%;
  max-width: 1280px;
  box-sizing: border-box;
  margin: 0 auto;
  padding: 110px 48px 72px;

  @media (max-width: 1024px) {
    padding: 96px 32px 56px;
  }

  @media (max-width: 768px) {
    padding: 80px 20px 40px;
  }
`;

const HeroContentRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 56px;
  width: 100%;

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 32px;
  }
`;

const HeroCopyColumn = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 560px;

  @media (max-width: 1024px) {
    max-width: unset;
  }
`;

const HeroTestimonialsColumn = styled.aside`
  flex: 0 0 390px;
  max-width: 450px;
  width: 100%;
  border-radius: 32px;
  padding: 0 16px 0 8px;
  position: relative;
  overflow: visible;

  @media (max-width: 1024px) {
    flex: unset;
    max-width: 100%;
    padding: 0;
  }
`;

const HeroTestimonialsHeading = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const HeroTestimonialsSubheading = styled.p`
  margin: 6px 0 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[600]};
`;

const HeroTestimonialsViewport = styled.div`
  position: relative;
  margin-top: 24px;
  padding: 32px 0 24px;
`;

const HeroTestimonialsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 360px;
  overflow-y: auto;
  padding-right: 8px;
  scroll-behavior: smooth;
  scroll-snap-type: y proximity;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const HeroTestimonialsPlaceholder = styled.p`
  margin: 24px 0 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[600]};
  text-align: center;
`;

const VerticalArrowButton = styled.button`
  position: absolute;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 38px;
  height: 38px;
  border-radius: 999px;
  border: none;
  background-color: #ffffff;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  color: ${({ theme }) => theme.colors.grey[800]};
  transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;

  &:hover {
    transform: translate(-50%, -50%) scale(1.05);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.24);
  }

  &:active {
    transform: translate(-50%, -50%) scale(0.96);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 640px) {
    width: 34px;
    height: 34px;
  }
`;

const VerticalArrowTop = styled(VerticalArrowButton)`
  top: 5px;
`;

const VerticalArrowBottom = styled(VerticalArrowButton)`
  bottom: -5px;
  transform: translate(-50%, 50%);

  &:hover {
    transform: translate(-50%, 50%) scale(1.05);
  }

  &:active {
    transform: translate(-50%, 50%) scale(0.96);
  }
`;

const BadgeRow = styled.div`
  font-size: 13px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[600]};
  margin-bottom: 18px;
`;

const Heading = styled.h1`
  max-width: 520px;
  font-size: 44px;
  line-height: 1.15;
  margin: 0 0 12px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const HeadingEmphasis = styled.span`
  font-style: italic;
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
`;

const SubHeading = styled.p`
  max-width: 420px;
  font-size: 16px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.grey[700]};
  margin: 0 0 32px;
`;

const PrimaryCta = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 16px 40px;
  border-radius: 999px;
  border: none;
  background: linear-gradient(135deg, #fb923c, #f97316);
  color: white;
  font-size: 18px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  cursor: pointer;
  box-shadow: 0 18px 28px rgba(0, 0, 0, 0.22);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.03);
    box-shadow: 0 22px 36px rgba(0, 0, 0, 0.26);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 14px 20px rgba(0, 0, 0, 0.18);
  }
`;

const CtaGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 14px;
`;

const CtaNote = styled.p`
  margin-top: 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[700]};
`;

const TrustRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[500]};
`;

const TrustLogo = styled.img`
  width: 90px;
  height: auto;
  object-fit: contain;
`;

const StepsStrip = styled.section`
  position: relative;
  z-index: 1;
  width: 100%;
  padding: 28px 32px 32px;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 768px) {
    padding: 24px 20px 24px;
  }
`;

const StepsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 32px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const Step = styled.div`
  text-align: center;
`;

const StepTitle = styled.h3`
  font-size: 18px;
  margin: 0 0 8px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
`;

const StepBody = styled.p`
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.grey[700]};
  margin: 0;
`;

const FeaturesSection = styled.section`
  position: relative;
  z-index: 1;
  width: 100%;
  padding: 36px 32px 40px;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 768px) {
    padding: 28px 20px 32px;
    gap: 12px;
  }
`;

const FeaturesEyebrow = styled.span`
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[600]};
`;

const FeaturesHeading = styled.h2`
  margin: 0;
  font-size: 28px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.article`
  border: 1px solid rgba(0, 0, 0, 0.04);
  border-radius: 20px;
  padding: 24px;
  background: #ffffff;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FeatureIcon = styled.span`
  font-size: 24px;
`;

const FeatureTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FeatureBody = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.grey[700]};
`;

const PageBottomSpacer = styled.div`
  height: 80px;

  @media (max-width: 768px) {
    height: 56px;
  }
`;

const TestimonialCard = styled.article`
  min-width: 280px;
  max-width: 360px;
  flex: 0 0 auto;
  scroll-snap-align: center;
  background-color: #ffffff;
  border-radius: 24px;
  border: 0.5px solid ${({ theme }) => theme.colors.primary};
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.08);
  padding: 20px 22px 22px;
  display: flex;
  flex-direction: column;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`;

const CardHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const ProductImageWrapper = styled.div`
  width: 96px;
  height: 96px;
  border-radius: 18px;
  overflow: hidden;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AvatarCircle = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PersonText = styled.div`
  display: flex;
  flex-direction: column;
`;

const PersonName = styled.span`
  font-size: 14px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 2px;
`;

const PersonLocation = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const QuoteText = styled.p`
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  margin-top: 4px;
`;

const HeroTestimonialCard = styled(TestimonialCard)`
  width: 100%;
  min-width: unset;
  max-width: unset;
  flex: 1 0 auto;
  padding: 15px 24px 17px;
`;

const FaqSection = styled.section`
  position: relative;
  z-index: 1;
  width: 100%;
  padding: 28px 32px 36px;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 768px) {
    padding: 24px 20px 32px;
  }
`;

const FaqHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FaqTitle = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FaqSubtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.grey[700]};
`;

const FaqList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  align-items: stretch;
`;

const FaqItem = styled.article`
  width: 100%;
  max-width: unset;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 18px;
  padding: 12px 18px;
  background-color: #ffffff;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06);
`;

const FaqQuestion = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  background: none;
  border: none;
  padding: 8px 0;
  text-align: left;
  cursor: pointer;
  font-size: 16px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FaqQuestionPrefix = styled.span`
  font-size: 18px;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
`;

const FaqToggleIcon = styled.span`
  margin-left: auto;
  font-size: 18px;
  color: ${({ theme }) => theme.colors.grey[600]};
`;

const FaqAnswer = styled.div`
  margin-top: 8px;
  padding-left: 26px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FaqAnswerText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.grey[700]};
`;

const EmailCaptureSection = styled.section`
  position: relative;
  z-index: 1;
  width: 100%;
  background: linear-gradient(135deg, #111827, #1f2937);
  color: #ffffff;
  padding: 36px 32px 44px;
  box-shadow: 0 -6px 16px rgba(0, 0, 0, 0.08);
  max-width: 1200px;
  margin: 0 auto;
  border-radius: 0 0 32px 32px;

  @media (max-width: 768px) {
    padding: 28px 20px 36px;
    border-radius: 0;
  }
`;

const EmailCaptureTitle = styled.h2`
  margin: 0 0 8px;
  font-size: 26px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
`;

const EmailCaptureDescription = styled.p`
  margin: 0 0 20px;
  font-size: 15px;
  color: rgba(255, 255, 255, 0.9);
  max-width: 520px;
`;

const EmailCaptureForm = styled.form`
  display: flex;
  gap: 12px;
  align-items: center;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const EmailInput = styled.input`
  flex: 1;
  padding: 14px 20px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.4);
+  background-color: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  font-size: 16px;
  outline: none;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.7);
  }

  &:focus {
    border-color: rgba(255, 255, 255, 0.9);
    background-color: rgba(255, 255, 255, 0.2);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const EmailSubmitButton = styled.button`
  padding: 14px 32px;
  border-radius: 999px;
  border: none;
  background: linear-gradient(135deg, #fb923c, #f97316);
  color: white;
  font-size: 16px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.25);

  &:hover {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const EmailHelperText = styled.p`
  margin: 12px 0 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
`;

const EmailStatus = styled.p<{ $state: 'success' | 'error' }>`
  margin: 6px 0 0;
  font-size: 13px;
  color: ${({ $state }) => ($state === 'success' ? '#86efac' : '#fecaca')};
`;

const HakkimizdaPage: React.FC = () => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [isLoadingTestimonials, setIsLoadingTestimonials] = useState(true);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [emailInput, setEmailInput] = useState('');
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);
  const [emailSubmitState, setEmailSubmitState] = useState<'idle' | 'success' | 'error'>(
    'idle'
  );
  const [emailSubmitMessage, setEmailSubmitMessage] = useState('');
  const heroTestimonialsRef = useRef<HTMLDivElement | null>(null);
  const testimonialCardHeightRef = useRef(0);
  const emailCaptureRef = useRef<HTMLElement | null>(null);
  const featureCards = [
    {
      icon: '🔍',
      title: 'Doğal Dil Arama',
      body: 'Ürünü tarif ederek bul: "perde bağlamak için kullanılan şey", "saçımı tepede topuz yapan sünger", "kapı altı rüzgarlık".',
    },
    {
      icon: '⚡',
      title: 'Hızlı & Doğru Öneriler',
      body: 'Amazon veri setiyle eşleşen en doğru seçenekleri birkaç saniyede listeler.',
    },
    {
      icon: '🤖',
      title: 'Yapay Zeka Destekli',
      body: 'Ürünü tarifinden anlamlandırır, marka veya modeli bilmesen de seni doğru ürüne götürür.',
    },
    {
      icon: '💸',
      title: 'Ücretsiz & Hesap Gerektirmez',
      body: 'Hemen dene; kayıt formu, kredi kartı veya üyelik şartı yok.',
    },
  ];

  type FaqItem = {
    question: string;
    answer: React.ReactNode[];
  };

  const faqItems: FaqItem[] = [
    {
      question: 'Sezgi nasıl çalışıyor?',
      answer: [
        'Sezgi, yazdığın tariften ne demek istediğini yapay zeka ile anlayarak Amazon\'daki en uygun ürünleri otomatik olarak bulur.',
      ],
    },
    {
      question: 'Ürünleri nasıl buluyorsunuz?',
      answer: [
        'Sezgi, Amazon’un geniş ürün kataloğuna bağlı çalışır. Sen ürünü tarif edersin, biz de Amazon’daki binlerce seçenek arasından en uygun olanları bulup sana getiririz. Önerdiğimiz tüm ürünler Amazon’da satılan gerçek ürünlerdir.',
      ],
    },
    {
      question: 'Ücretli mi?',
      answer: [
        'Hayır. Sezgi\'yi kullanmak tamamen ücretsizdir.',
        'Üye olman da gerekmez.',
      ],
    },
    {
      question: 'Neden tarif ederek aramak daha kolay?',
      answer: [
        'Klasik e-ticaret aramaları genelde teknik veya spesifik kelimeler ister.',
        'Sezgi ise "şu şey", "kapı altına konan sünger", "perdeyi bağlayan metal şey" gibi doğal cümleleri anlayarak doğru ürünü bulur.',
      ],
    },
    {
      question: 'Gizlilik politikası nedir?',
      answer: [
        'Tüm aramalar anonimdir ve kişisel bilgiler saklanmaz.',
        <>
          Detaylı bilgi:{' '}
          <a href="https://sezgi.co/gizlilik-politikasi" target="_blank" rel="noopener noreferrer">
            sezgi.co/gizlilik-politikasi
          </a>
        </>,
      ],
    },
    {
      question: 'Sezgi ürün bulamazsa ne olur?',
      answer: [
        'Aradığın ürün Amazon\'da yoksa, Sezgi en yakın alternatifleri getirir.',
      ],
    },
  ];

  const handleStartClick = () => {
    trackEvent('cta_click', {
      cta_id: 'hakkimizda_hero_hemen_dene',
      page_path: '/hakkimizda',
    });

    if (emailCaptureRef.current) {
      emailCaptureRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      return;
    }

    window.scrollTo({
      top: document.body.scrollHeight,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    const loadTestimonials = async () => {
      try {
        setIsLoadingTestimonials(true);
        const data = await getRandomTestimonials(7);

        setTestimonials(data && data.length > 0 ? data : []);
      } catch (error) {
        console.error('Failed to load testimonials for Hakkimizda page.', error);
        setTestimonials([]);
      } finally {
        setIsLoadingTestimonials(false);
      }
    };

    loadTestimonials();
  }, []);

  useEffect(() => {
    if (!heroTestimonialsRef.current) return;
    const track = heroTestimonialsRef.current;
    const cardElements = track.querySelectorAll('[data-testimonial-card]');
    const firstCard = cardElements[0] as HTMLElement | undefined;
    const secondCard = cardElements[1] as HTMLElement | undefined;

    if (!firstCard) {
      testimonialCardHeightRef.current = track.clientHeight || 0;
      return;
    }

    if (secondCard) {
      const rawGap = secondCard.offsetTop - firstCard.offsetTop - firstCard.offsetHeight;
      const gap = rawGap > 0 ? rawGap : 0;
      testimonialCardHeightRef.current = firstCard.offsetHeight + gap;
    } else {
      testimonialCardHeightRef.current = firstCard.offsetHeight;
    }
  }, [testimonials.length]);

  const scrollTestimonialsColumn = (direction: 'next' | 'prev') => {
    if (!heroTestimonialsRef.current) return;
    const track = heroTestimonialsRef.current;
    const scrollAmount =
      testimonialCardHeightRef.current || Math.max(160, track.clientHeight * 0.7);
    const maxScroll = Math.max(0, track.scrollHeight - track.clientHeight);
    const target =
      direction === 'next' ? track.scrollTop + scrollAmount : track.scrollTop - scrollAmount;
    const clamped = Math.max(0, Math.min(target, maxScroll));

    track.scrollTo({
      top: clamped,
      behavior: 'smooth',
    });
  };

  const handleScrollDownTestimonials = () => {
    scrollTestimonialsColumn('next');
  };

  const handleScrollUpTestimonials = () => {
    scrollTestimonialsColumn('prev');
  };

  const toggleFaqItem = (index: number) => {
    setOpenFaqIndex((prev) => (prev === index ? null : index));
  };

  const handleEmailSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalized = emailInput.trim();

    if (!normalized) {
      setEmailSubmitState('error');
      setEmailSubmitMessage('Lütfen e-posta adresini yaz.');
      return;
    }

    const basicEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!basicEmailRegex.test(normalized)) {
      setEmailSubmitState('error');
      setEmailSubmitMessage('Geçerli bir e-posta adresi yaz.');
      return;
    }

    try {
      setIsSubmittingEmail(true);
      setEmailSubmitState('idle');
      setEmailSubmitMessage('');
      await submitContactEmail(normalized);
      trackEvent('email_signup', {
        email_domain: normalized.split('@')[1] || 'unknown',
        source: 'hakkimizda_beta_waitlist',
        page_path: '/hakkimizda',
      });
      setEmailSubmitState('success');
      setEmailSubmitMessage('Harika! Beta açıldığında sana haber vereceğiz.');
      setEmailInput('');
    } catch (error) {
      console.error('Failed to submit contact email.', error);
      const axiosLikeError = error as { response?: { data?: { detail?: string } } };
      const backendDetail =
        typeof axiosLikeError?.response?.data?.detail === 'string'
          ? axiosLikeError.response?.data?.detail
          : undefined;

      setEmailSubmitState('error');
      setEmailSubmitMessage(
        backendDetail || 'Şu anda kaydedemedik. Lütfen biraz sonra tekrar dene.'
      );
    } finally {
      setIsSubmittingEmail(false);
    }
  };

  return (
    <PageWrapper>
      <HeroWrapper>
        <Overlay />
        <Content>
          <HeroContentRow>
            <HeroCopyColumn>
              <BadgeRow>KOLAY ARAMA • DOĞRU ÜRÜN • STRESSİZ ALIŞVERİŞ</BadgeRow>
              <Heading>
                Aklındaki ürünü <HeadingEmphasis>tarif ederek</HeadingEmphasis> bul.
              </Heading>
              <SubHeading>
                Dilinin ucundaki o ürünü, bir arkadaşına anlatır gibi birkaç kelimeyle tarif et;
                Sezgi senin için bulsun.
              </SubHeading>
              <CtaGroup>
                <PrimaryCta onClick={handleStartClick}>Hemen Dene*</PrimaryCta>
                <TrustRow>
                  <TrustLogo src="/amazon_logo.png" alt="Amazon" />
                  <span>Sonuçlar Amazon üzerinden listelenir.</span>
                </TrustRow>
              </CtaGroup>
              <CtaNote>*Ücretsiz deneme. Hiçbir taahhüt yok.</CtaNote>
            </HeroCopyColumn>

            <HeroTestimonialsColumn>
              <HeroTestimonialsHeading>Sezgi&apos;yi deneyenler ne diyor?</HeroTestimonialsHeading>
              <HeroTestimonialsSubheading>
                Gerçek kullanıcıların tarif ederek alışveriş deneyimleri.
              </HeroTestimonialsSubheading>

              {testimonials.length > 0 ? (
                <HeroTestimonialsViewport>
                  <VerticalArrowTop
                    type="button"
                    aria-label="Yorumlarda yukarı kaydır"
                    onClick={handleScrollUpTestimonials}
                  >
                    ↑
                  </VerticalArrowTop>
                  <HeroTestimonialsList ref={heroTestimonialsRef}>
                    {testimonials.map((t) => (
                      <HeroTestimonialCard data-testimonial-card key={t.id}>
                        <CardHeader>
                          <CardHeaderLeft>
                            <AvatarCircle>
                              <AvatarImage src={t.avatarUrl} alt={t.name} />
                            </AvatarCircle>
                            <PersonText>
                              <PersonName>{t.name}</PersonName>
                              <PersonLocation>{t.location}</PersonLocation>
                            </PersonText>
                          </CardHeaderLeft>
                          {t.productImageUrl && (
                            <ProductImageWrapper>
                              <ProductImage
                                src={t.productImageUrl}
                                alt={`${t.name} için önerilen ürün`}
                              />
                            </ProductImageWrapper>
                          )}
                        </CardHeader>
                        <QuoteText>{t.quote}</QuoteText>
                      </HeroTestimonialCard>
                    ))}
                  </HeroTestimonialsList>
                  <VerticalArrowBottom
                    type="button"
                    aria-label="Yorumlarda aşağı kaydır"
                    onClick={handleScrollDownTestimonials}
                  >
                    ↓
                  </VerticalArrowBottom>
                </HeroTestimonialsViewport>
              ) : (
                <HeroTestimonialsPlaceholder>
                  {isLoadingTestimonials
                    ? 'Yorumlar yükleniyor...'
                    : 'Henüz hikaye paylaşan olmadı, yakında burada göreceksin.'}
                </HeroTestimonialsPlaceholder>
              )}
            </HeroTestimonialsColumn>
          </HeroContentRow>
        </Content>
      </HeroWrapper>

      <StepsStrip>
        <StepsRow>
          <Step>
            <StepTitle>TARİF ET</StepTitle>
            <StepBody>
              Aklındaki şeyi, sanki bir arkadaşına anlatır gibi birkaç kelimeyle yaz.
            </StepBody>
          </Step>
          <Step>
            <StepTitle>SEÇ</StepTitle>
            <StepBody>
              Sezgi&apos;nin senin için bulduğu ve resmettiği kavramlardan doğru olanı tek tıkla
              seç.
            </StepBody>
          </Step>
          <Step>
            <StepTitle>KEŞFET</StepTitle>
            <StepBody>
              Artık adını bildiğin o ürünün en iyi alternatiflerini anında karşına getirelim.
            </StepBody>
          </Step>
        </StepsRow>
      </StepsStrip>

      <FeaturesSection>
        <FeaturesHeading>Sezgi neden bu kadar kolay?</FeaturesHeading>
        <FeatureGrid>
          {featureCards.map((feature) => (
            <FeatureCard key={feature.title}>
              <FeatureIcon aria-hidden="true">{feature.icon}</FeatureIcon>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureBody>{feature.body}</FeatureBody>
            </FeatureCard>
          ))}
        </FeatureGrid>
      </FeaturesSection>

      <FaqSection>
        <FaqHeader>
          <FaqTitle>Sık Sorulan Sorular (SSS)</FaqTitle>
        </FaqHeader>
        <FaqList>
          {faqItems.map((item, index) => {
            const isOpen = openFaqIndex === index;
            return (
              <FaqItem key={item.question}>
                <FaqQuestion
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() => toggleFaqItem(index)}
                >
                  <FaqQuestionPrefix aria-hidden="true">&gt;</FaqQuestionPrefix>
                  <span>{item.question}</span>
                  <FaqToggleIcon aria-hidden="true">{isOpen ? '−' : '+'}</FaqToggleIcon>
                </FaqQuestion>
                {isOpen && (
                  <FaqAnswer>
                    {item.answer.map((paragraph, paragraphIndex) => (
                      <FaqAnswerText key={`${item.question}-${paragraphIndex}`}>
                        {paragraph}
                      </FaqAnswerText>
                    ))}
                  </FaqAnswer>
                )}
              </FaqItem>
            );
          })}
        </FaqList>
      </FaqSection>

      <EmailCaptureSection ref={emailCaptureRef}>
        <EmailCaptureTitle>Sezgi yayına girdiğinde ilk sen haberdar ol</EmailCaptureTitle>
        <EmailCaptureDescription>
          Beta açılışını e-posta ile bildiriyoruz. Mailini bırak, olduğunda sana haber verelim.
        </EmailCaptureDescription>
        <EmailCaptureForm onSubmit={handleEmailSubmit}>
          <EmailInput
            type="email"
            name="contact-email"
            placeholder="ornek@mail.com"
            value={emailInput}
            onChange={(event) => setEmailInput(event.target.value)}
            disabled={isSubmittingEmail}
            aria-label="E-posta adresi"
            required
          />
          <EmailSubmitButton type="submit" disabled={isSubmittingEmail}>
            {isSubmittingEmail ? 'Gönderiliyor...' : 'Kaydol'}
          </EmailSubmitButton>
        </EmailCaptureForm>
        <EmailHelperText>
        Mail adresini bırakarak gizlilik politikasını kabul etmiş olursun. Sadece beta erişimini duyurmak için bir kez iletişime geçeceğiz. Herhangi bir promosyon veya pazarlama maili göndermiyoruz.
        </EmailHelperText>
        {emailSubmitMessage && emailSubmitState !== 'idle' && (
          <EmailStatus
            role="status"
            aria-live="polite"
            $state={emailSubmitState === 'success' ? 'success' : 'error'}
          >
            {emailSubmitMessage}
          </EmailStatus>
        )}
      </EmailCaptureSection>

      <PageBottomSpacer />
    </PageWrapper>
  );
};

export default HakkimizdaPage;


